import { atom, getDefaultStore } from 'jotai';

import {
  type SESSION_SOURCE_TYPE,
  type SessionSourceType,
} from '@/utils/analytics/session-source-type';

/**
 * Chronicle-origin session metadata for this app run (iOS parity —
 * end-to-end device-file→note tracing + Datadog `session_source_type`).
 *
 * Populated when a Chronicle-origin session is created this app run:
 * - bulk sync — `session_engine:session_created` (task_id = device filename)
 * - live recording — `chronicle:auto_session_created`
 *
 * Both writers share one map, so readers must carry `sourceType` — stamping
 * a single hardcoded facet (e.g. always `chronicleLiveRecording`) mislabels
 * bulk-sync note rows in Datadog.
 *
 * Read at analytics fire time to attach `device_file_name` / source type to
 * downstream session-pipeline events. Sessions not in the map are mic/upload
 * sessions — the properties are simply absent for them (no noise).
 *
 * Session-scoped and in-memory only: a session synced in a previous app run
 * won't be traceable, matching iOS (its device state is also in-memory).
 */
export type ChronicleSessionDeviceFileEntry = {
  deviceFileName: string;
  /** Live BLE vs bulk sync — drives Datadog `@session_source_type`. */
  sourceType:
    | typeof SESSION_SOURCE_TYPE.ChronicleLiveRecording
    | typeof SESSION_SOURCE_TYPE.BulkSync;
};

export const chronicleSessionDeviceFilesAtom = atom<
  ReadonlyMap<string, ChronicleSessionDeviceFileEntry>
>(new Map<string, ChronicleSessionDeviceFileEntry>());

/** Write-only action: register a sessionId → device-file + source pairing. */
export const registerChronicleSessionDeviceFileAtom = atom(
  null,
  (
    get,
    set,
    pairing: {
      sessionId: string;
      deviceFileName: string;
      sourceType: ChronicleSessionDeviceFileEntry['sourceType'];
    }
  ) => {
    const prev = get(chronicleSessionDeviceFilesAtom);
    const existing = prev.get(pairing.sessionId);
    if (
      existing?.deviceFileName === pairing.deviceFileName &&
      existing.sourceType === pairing.sourceType
    ) {
      return;
    }
    const next = new Map(prev);
    next.set(pairing.sessionId, {
      deviceFileName: pairing.deviceFileName,
      sourceType: pairing.sourceType,
    });
    set(chronicleSessionDeviceFilesAtom, next);
  }
);

/**
 * Non-React reader for analytics call sites (mirrors how
 * `getChronicleDeviceProps` reads device state at fire time).
 */
export function getChronicleDeviceFileNameForSession(
  sessionId: string | undefined
): string | undefined {
  if (!sessionId) return undefined;
  return getDefaultStore().get(chronicleSessionDeviceFilesAtom).get(sessionId)
    ?.deviceFileName;
}

/**
 * Datadog `@session_source_type` for a Chronicle-origin session registered
 * this app run. Undefined when the session is not in the map (mic/upload, or
 * registered in a previous run).
 */
export function getChronicleSessionSourceTypeForSession(
  sessionId: string | undefined
): SessionSourceType | undefined {
  if (!sessionId) return undefined;
  return getDefaultStore().get(chronicleSessionDeviceFilesAtom).get(sessionId)
    ?.sourceType;
}
